/**
 * 바우처 목록 페이지
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import DataTable, { Column } from '../../components/common/DataTable';
import Pagination from '../../components/common/Pagination';
import FilterBar, { FilterField } from '../../components/common/FilterBar';
import { voucherService } from '../../services/voucherService';
import { Voucher, VoucherFilter, VoucherStatus } from '../../types/voucher';
import { format } from 'date-fns';

const VouchersList: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<VoucherFilter>({
    page: 1,
    limit: 20,
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['vouchers', filter],
    queryFn: () => voucherService.getVouchers(filter),
  });

  const getStatusBadge = (status: VoucherStatus) => {
    const statusMap: Record<VoucherStatus, { variant: string; label: string }> = {
      issued: { variant: 'success', label: '발행됨' },
      exchanged: { variant: 'info', label: '교환됨' },
      expired: { variant: 'warning', label: '만료됨' },
      cancelled: { variant: 'danger', label: '취소됨' },
    };

    const statusInfo = statusMap[status] || { variant: 'secondary', label: status };
    return <Badge bg={statusInfo.variant}>{statusInfo.label}</Badge>;
  };

  const isExpired = (validTo: string) => {
    return new Date(validTo) < new Date();
  };

  const columns: Column<Voucher>[] = [
    {
      key: 'voucher_number',
      label: '바우처번호',
      render: (voucher) => (
        <span style={{ fontWeight: 600, color: 'var(--color-accent-cyan)' }}>
          {voucher.voucher_number}
        </span>
      ),
    },
    {
      key: 'voucher_code',
      label: '바우처 코드',
      render: (voucher) => (
        <code style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>
          {voucher.voucher_code}
        </code>
      ),
    },
    {
      key: 'booking_number',
      label: '예약번호',
      render: (voucher) => voucher.booking?.booking_number || '-',
    },
    {
      key: 'valid_period',
      label: '유효기간',
      render: (voucher) => (
        <div>
          <div>{format(new Date(voucher.valid_from), 'yyyy-MM-dd')}</div>
          <div className={isExpired(voucher.valid_to) ? 'text-danger' : 'text-muted'}>
            ~ {format(new Date(voucher.valid_to), 'yyyy-MM-dd')}
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      label: '상태',
      render: (voucher) => getStatusBadge(voucher.status),
    },
    {
      key: 'exchanged_at',
      label: '교환일시',
      render: (voucher) => voucher.exchanged_at
        ? format(new Date(voucher.exchanged_at), 'yyyy-MM-dd HH:mm')
        : '-',
    },
    {
      key: 'created_at',
      label: '발행일시',
      render: (voucher) => format(new Date(voucher.created_at), 'yyyy-MM-dd HH:mm'),
    },
  ];

  const filterFields: FilterField[] = [
    {
      name: 'status',
      label: '상태',
      type: 'select',
      options: [
        { value: 'issued', label: '발행됨' },
        { value: 'exchanged', label: '교환됨' },
        { value: 'expired', label: '만료됨' },
        { value: 'cancelled', label: '취소됨' },
      ],
    },
    {
      name: 'valid_from',
      label: '유효기간',
      type: 'dateRange',
    },
    {
      name: 'voucher_number',
      label: '바우처번호',
      type: 'text',
      placeholder: '바우처번호 검색',
    },
    {
      name: 'voucher_code',
      label: '바우처 코드',
      type: 'text',
      placeholder: '바우처 코드 검색',
    },
    {
      name: 'booking_number',
      label: '예약번호',
      type: 'text',
      placeholder: '예약번호 검색',
    },
  ];

  const handleFilterChange = (newFilter: Record<string, any>) => {
    setFilter({
      ...filter,
      ...newFilter,
      page: 1,
    });
  };

  const handlePageChange = (page: number) => {
    setFilter({ ...filter, page });
  };

  const handleRowClick = (voucher: Voucher) => {
    navigate(`/admin/vouchers/${voucher.id}`);
  };

  return (
    <AdminLayout>
      <div>
        <h1 className="mb-4" style={{ color: '#fff' }}>바우처 관리</h1>

        <Card className="mb-4">
          <Card.Body>
            <FilterBar
              fields={filterFields}
              values={filter}
              onChange={handleFilterChange}
              onSearch={() => refetch()}
              onReset={() => setFilter({ page: 1, limit: 20 })}
            />
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                총 <strong>{data?.pagination.total_count || 0}</strong>건
              </div>
            </div>

            <DataTable
              columns={columns}
              data={data?.vouchers || []}
              loading={isLoading}
              emptyMessage="바우처 내역이 없습니다."
              onRowClick={handleRowClick}
            />

            {data && data.pagination.total_pages > 1 && (
              <Pagination
                currentPage={data.pagination.current_page}
                totalPages={data.pagination.total_pages}
                onPageChange={handlePageChange}
              />
            )}
          </Card.Body>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default VouchersList;

